'use client';

import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthErrorCode } from '@workspace/auth/errors';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@workspace/ui/components/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormProvider
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';

import { signInWithExternalCredentials } from '~/actions/auth/sign-in-with-external-credentials';
import { authErrorLabels } from '~/lib/labels';
import { externalLogInSchema } from '~/schemas/auth/external-login-schema';

type FormValues = z.infer<typeof externalLogInSchema>;

export function ExternalSignInCard(): React.JSX.Element {
  const [serverError, setServerError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(externalLogInSchema),
    defaultValues: { orgSlug: '', username: '', password: '' },
    mode: 'onSubmit'
  });

  async function onSubmit(values: FormValues) {
    setServerError(null);
    const result = await signInWithExternalCredentials(values);

    if (result?.validationErrors?._errors?.length) {
      const code = result.validationErrors._errors[0] as AuthErrorCode;
      const message =
        (authErrorLabels as Record<string, string>)[code] ??
        'Invalid username or password.';
      setServerError(message);
      return;
    }

    if (result?.validationErrors) return;

    if (result?.serverError) {
      setServerError('Something went wrong. Please try again.');
    }
  }

  const isSubmitting = form.formState.isSubmitting;

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardHeader>
        <CardTitle>External Sign In</CardTitle>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="orgSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization Slug</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="acme"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="username"
                      placeholder="jdoe"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError ? (
              <p className="text-sm text-red-600">{serverError}</p>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Sign in
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => (window.location.href = '/auth/sign-in')}
            >
              Back to standard sign in
            </Button>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
